import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, ChevronDown, ChevronsDown, CircleDot, Ellipsis, Flag, FolderInput, GitBranch, MessageCircle, Plus, Trash2, UserPlus, UserRoundPlus, X } from 'lucide-react';
import { flattenPages, keyFor } from '@missions/api-client';
import type { CustomField, MissionSummary, Project } from '@missions/types';
import { useDialogs, useToast } from '@missions/ui';
import { api } from '../../lib/client';
import { useErrorToast, useInfinite } from '../../lib/hooks';
import { useSession, useT } from '../../lib/session';
import { AvStack, Due, Empty, PRIORITY, Pri, Radar, SDot, Tag, etaLabel } from '../../components/atoms';
import { Menu, type MenuItem } from '../../components/Menu';
import { useShell } from '../../layout/shell-context';
import { celebrate, projectCompleteBanner, useMemberOptions, useMissionInvalidate, useProjects } from '../../features/missions/data';
import { type ListState, filterJson } from './useProjectData';

const cfWidth = (f: CustomField) => (f.type === 'labels' || f.type === 'people' ? 170 : f.type === 'url' ? 180 : 140);

function cfCell(f: CustomField, v: unknown) {
  const empty = <span className="empty-cell">—</span>;
  if (v == null || v === '' || (Array.isArray(v) && !v.length)) return empty;
  const opt = (id: string) => f.options.find((o) => o.id === id);
  const pill = (id: string) => {
    const o = opt(id);
    return o ? <span className="apill" key={id} style={{ ['--pc' as string]: o.color } as CSSProperties}><span className="dot" style={{ background: o.color }} />{o.label}</span> : null;
  };
  switch (f.type) {
    case 'dropdown':
      return typeof v === 'string' ? pill(v) : empty;
    case 'labels':
      return Array.isArray(v) ? <span className="chips">{v.map((x) => pill(String(x)))}</span> : empty;
    case 'currency':
      return <span className="bud">₹{Number(v).toLocaleString('en-IN')}</span>;
    case 'number':
      return <span className="bud">{String(v)}</span>;
    case 'url':
      return <a className="url" href={String(v)} target="_blank" rel="noopener noreferrer" data-tooltip={String(v)}><span>{String(v).replace(/^https?:\/\//, '')}</span></a>;
    case 'checkbox':
      return <span>{v ? '✓' : '—'}</span>;
    case 'progress':
      return <><span className="pbar" style={{ maxWidth: 70 }}><i style={{ width: `${Number(v)}%`, background: 'var(--brand)' }} /></span><span className="eta">{String(v)}%</span></>;
    case 'rating':
      return <span className="stars-ro">{'★'.repeat(Number(v))}<span>{'★'.repeat(Math.max(0, 5 - Number(v)))}</span></span>;
    default:
      return <span className="ui-truncate">{String(v)}</span>;
  }
}

interface RowProps {
  m: MissionSummary;
  project: Project;
  fields: CustomField[];
  selected: boolean;
  onSelect: (id: string, on: boolean) => void;
  canBulk: boolean;
}

function Row({ m, project, fields, selected, onSelect, canBulk }: RowProps) {
  const shell = useShell();
  const t = useT();
  const s = useSession();
  const toast = useToast();
  const onErr = useErrorToast();
  const refresh = useMissionInvalidate();
  const dialogs = useDialogs();
  const status = project.statuses.find((x) => x.id === m.statusId);
  const sRef = useRef<HTMLButtonElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const [sOpen, setSOpen] = useState(false);
  const [mOpen, setMOpen] = useState(false);
  const setStatus = async (statusId: string) => {
    const to = project.statuses.find((x) => x.id === statusId);
    const prev = m.statusId;
    try {
      await api.call('missions.update', { params: { id: m.id }, body: { statusId } });
      if (to?.isClosed && sRef.current) {
        celebrate(sRef.current);
        toast(`${t.itemCompleted}: ${m.title}`, { action: { label: 'Undo', onClick: () => void api.call('missions.update', { params: { id: m.id }, body: { statusId: prev } }).then(() => refresh()) } });
      }
      await refresh();
      if (to?.isClosed) {
        const p = await api.call('projects.get', { params: { id: project.id } });
        if (p.counts.open === 0) projectCompleteBanner(project.name, t.projectCompleted, t.projectCompletedSub);
      }
    } catch (e) {
      onErr(e);
    }
  };
  const statusItems: MenuItem[] = [...project.statuses].sort((a, b) => a.sortOrder - b.sortOrder).map((x) => ({ label: x.name, dot: x.color, selected: x.id === m.statusId, onClick: () => void setStatus(x.id) }));
  const moreItems: MenuItem[] = [
    { label: `Open ${t.item}`, onClick: () => shell.openMission(m.id) },
    {
      label: 'Copy link',
      onClick: () => {
        void navigator.clipboard?.writeText(`${location.origin}${location.pathname}?m=${m.id}`);
        toast(`${t.Item} link copied`);
      },
    },
    { label: 'Duplicate', disabled: !s.can('mission.create'), lock: 'Member', onClick: () => void api.call('missions.duplicate', { params: { id: m.id } }).then(() => refresh()).catch(onErr) },
    '-',
    {
      label: `Delete ${t.item}`,
      icon: <Trash2 />,
      danger: true,
      disabled: !s.can('mission.delete'),
      lock: 'Manager',
      onClick: async () => {
        if (!(await dialogs.confirm({ tone: 'danger', title: `Delete this ${t.item}?`, message: <><b>“{m.title}”</b> will be moved to Trash. You can restore it within 30 days.</>, confirmText: `Delete ${t.item}` }))) return;
        try {
          const r = await api.call('missions.delete', { params: { id: m.id } });
          await refresh();
          toast(`${t.Item} deleted`, { action: { label: 'Undo', onClick: () => void api.call('missions.undo', { body: { undoToken: r.undoToken } }).then(() => refresh()) } });
        } catch (e) {
          onErr(e);
        }
      },
    },
  ];
  const canEdit = s.can('mission.edit');
  return (
    <div className={`row${selected ? ' sel' : ''}${m.completedAt ? ' isdone' : ''}`} role="row" data-id={m.id}>
      <div className="c c-check">{canBulk && <input className="cb" type="checkbox" checked={selected} onChange={(e) => onSelect(m.id, e.target.checked)} aria-label={`Select ${m.title}`} />}</div>
      <div className="c c-name">
        <button ref={sRef} className="sbtn" disabled={!canEdit} onClick={() => setSOpen((o) => !o)} aria-label={`Status: ${status?.name ?? '—'}`}>
          <SDot s={status} />
        </button>
        <Menu anchorRef={sRef} open={sOpen} onClose={() => setSOpen(false)} title="Status" items={statusItems} minWidth={200} />
        <span className="tid">{m.key}</span>
        <button className="tname" onClick={() => shell.openMission(m.id)}>{m.title}</button>
        {m.counts.objectives > 0 && <span className="meta-sm" data-tooltip={t.subItems}><GitBranch />{m.counts.objectivesDone}/{m.counts.objectives}</span>}
        {m.counts.comments > 0 && <span className="meta-sm" data-tooltip="Comments"><MessageCircle />{m.counts.comments}</span>}
        {m.tags.map((g) => <Tag key={g.id} name={g.name} color={g.color} />)}
      </div>
      <div className="c c-asg">{m.assignees.length ? <AvStack users={m.assignees} /> : <span className="empty-cell" data-tooltip="Unassigned"><UserRoundPlus /></span>}</div>
      <div className="c c-due"><Due due={m.dueAt} done={!!m.completedAt} /></div>
      <div className="c c-pri"><Pri p={m.priority} /></div>
      <div className="c c-eta"><span className="eta">{etaLabel(m.etaMinutes)}</span></div>
      {fields.map((f, i) => (
        <div key={f.id} className={`c c-cf${i === 0 ? ' m-show' : ''}`}>{cfCell(f, m.customValues[f.id])}</div>
      ))}
      <div className="c c-more">
        <button ref={moreRef} onClick={() => setMOpen((o) => !o)} aria-label={`Actions for ${m.title}`} data-tooltip="More actions"><Ellipsis /></button>
        <Menu anchorRef={moreRef} open={mOpen} onClose={() => setMOpen(false)} title={m.title} items={moreItems} alignRight />
      </div>
    </div>
  );
}

const SkRow = ({ fields }: { fields: CustomField[] }) => (
  <div className="row">
    <div className="c c-check"><span className="sk ui-skel" style={{ width: 16, height: 16 }} /></div>
    <div className="c c-name"><span className="sk ui-skel" style={{ width: '60%' }} /></div>
    <div className="c c-asg"><span className="sk ui-skel" style={{ width: 24, height: 24, borderRadius: '50%' }} /></div>
    <div className="c c-due"><span className="sk ui-skel" style={{ width: 56 }} /></div>
    <div className="c c-pri"><span className="sk ui-skel" style={{ width: 60 }} /></div>
    <div className="c c-eta"><span className="sk ui-skel" style={{ width: 28 }} /></div>
    {fields.map((f) => <div className="c c-cf" key={f.id}><span className="sk ui-skel" style={{ width: '70%' }} /></div>)}
    <div className="c" />
  </div>
);

function Header({ fields, allSel, onAll, canBulk }: { fields: CustomField[]; allSel: boolean; onAll: (on: boolean) => void; canBulk: boolean }) {
  const t = useT();
  return (
    <div className="hrow" role="row">
      <div>{canBulk && <input type="checkbox" className="cb" checked={allSel} onChange={(e) => onAll(e.target.checked)} aria-label={`Select all ${t.items}`} />}</div>
      <div>{t.Item}</div>
      <div>Assignee</div>
      <div>Due date</div>
      <div>Priority</div>
      <div>ETA</div>
      {fields.map((f) => (
        <div key={f.id}><span className="colh" data-tooltip={f.name}><span>{f.name}</span></span></div>
      ))}
      <div />
    </div>
  );
}

interface GroupData {
  value: string;
  label: string;
  color: string | null;
  count: number;
  isClosed: boolean;
  items: MissionSummary[];
  nextCursor: string | null;
}

function GroupBlock({ g, base, project, fields, sel, setSel, canBulk, canCreate }: { g: GroupData; base: Record<string, unknown>; project: Project; fields: CustomField[]; sel: Set<string>; setSel: (fn: (s: Set<string>) => Set<string>) => void; canBulk: boolean; canCreate: boolean }) {
  const t = useT();
  const s = useSession();
  const onErr = useErrorToast();
  const refresh = useMissionInvalidate();
  const [collapsed, setCollapsed] = useState(false);
  const [extra, setExtra] = useState<MissionSummary[]>([]);
  const [cursor, setCursor] = useState(g.nextCursor);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const gmRef = useRef<HTMLButtonElement>(null);
  const [gm, setGm] = useState(false);
  useEffect(() => {
    setExtra([]);
    setCursor(g.nextCursor);
  }, [g]);
  const items = [...g.items, ...extra.filter((x) => !g.items.some((y) => y.id === x.id))];
  const loadMore = async () => {
    if (!cursor) return;
    setLoading(true);
    try {
      const r = await api.call('missions.list', { query: { ...base, groupValue: g.value, cursor, limit: 25 } as never });
      setExtra((x) => [...x, ...r.data]);
      setCursor(r.page.hasMore ? r.page.nextCursor : null);
    } catch (e) {
      onErr(e);
    } finally {
      setLoading(false);
    }
  };
  const save = async () => {
    const title = name.trim();
    if (!title) {
      setAdding(false);
      return;
    }
    try {
      await api.call('missions.create', { body: { projectId: project.id, title, statusId: base['groupBy'] === 'status' ? g.value : undefined } });
      setName('');
      await refresh();
    } catch (e) {
      onErr(e);
    }
  };
  const statusObj = project.statuses.find((x) => x.id === g.value);
  return (
    <div className={`group${collapsed ? ' collapsed' : ''}`} id={`g-${g.value}`}>
      <div className="gh">
        <button className="tgl" onClick={() => setCollapsed((c) => !c)} aria-expanded={!collapsed} aria-label={`Toggle ${g.label}`}><ChevronDown /></button>
        <span className="spill" style={{ background: g.color ?? 'var(--text-3)' }}>{g.isClosed ? t.closedGroup : g.label}</span>
        <span className="gcount">{g.count}</span>
        {statusObj && s.can('status.manage') && (
          <>
            <button ref={gmRef} className="gmenu" onClick={() => setGm((o) => !o)} aria-label={`${g.label} options`}><Ellipsis /></button>
            <GroupMenu anchor={gmRef} open={gm} onClose={() => setGm(false)} statusId={statusObj.id} name={statusObj.name} />
          </>
        )}
      </div>
      <div className="gbody">
        {items.map((m) => (
          <Row key={m.id} m={m} project={project} fields={fields} selected={sel.has(m.id)} canBulk={canBulk} onSelect={(id, on) => setSel((x) => { const n = new Set(x); if (on) n.add(id); else n.delete(id); return n; })} />
        ))}
        {canCreate &&
          (adding ? (
            <div className="addrow-input">
              <span className="sdot" style={{ borderColor: g.color ?? 'var(--text-3)' }} />
              <input
                autoFocus
                placeholder={`${t.Item} name. Enter to save, Esc to cancel`}
                aria-label={`New ${t.item} name`}
                maxLength={140}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void save();
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setAdding(false);
                    setName('');
                  }
                }}
              />
              <button className="btn btn-p" style={{ height: 36 }} onClick={() => void save()}>Save</button>
            </div>
          ) : (
            <button className="addrow" onClick={() => setAdding(true)}><Plus />{t.addItem}</button>
          ))}
        {cursor && (
          <div className="gfoot">
            Showing {items.length} of {g.count}
            <button className="more" onClick={() => void loadMore()} disabled={loading}>
              {loading ? <Radar size={16} /> : <ChevronsDown />}Load {Math.min(25, g.count - items.length)} more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GroupMenu({ anchor, open, onClose, statusId, name }: { anchor: React.RefObject<HTMLButtonElement | null>; open: boolean; onClose: () => void; statusId: string; name: string }) {
  const dialogs = useDialogs();
  const onErr = useErrorToast();
  const refresh = useMissionInvalidate();
  const toast = useToast();
  return (
    <Menu
      anchorRef={anchor}
      open={open}
      onClose={onClose}
      title={name}
      items={[
        {
          label: 'Rename status',
          onClick: async () => {
            const v = await dialogs.prompt({ title: 'Rename status', label: 'Status name', value: name, confirmText: 'Rename', validate: (x) => (x.length > 24 ? 'Keep it under 24 characters' : null) });
            if (v) void api.call('statuses.update', { params: { id: statusId }, body: { name: v } }).then(() => { void refresh(); toast(`Renamed to “${v}”`); }).catch(onErr);
          },
        },
      ]}
    />
  );
}

/** Bulk bar (mockups/app.html #bulk): status, assignee, due, priority, move, delete (+ Undo). */
function BulkBar({ ids, project, onClear }: { ids: string[]; project: Project; onClear: () => void }) {
  const t = useT();
  const s = useSession();
  const shell = useShell();
  const toast = useToast();
  const dialogs = useDialogs();
  const onErr = useErrorToast();
  const refresh = useMissionInvalidate();
  const members = useMemberOptions();
  const { flat } = useProjects(shell.workspaceId);
  const refs = { status: useRef<HTMLButtonElement>(null), assignee: useRef<HTMLButtonElement>(null), due: useRef<HTMLButtonElement>(null), priority: useRef<HTMLButtonElement>(null), move: useRef<HTMLButtonElement>(null) };
  const [open, setOpen] = useState<keyof typeof refs | null>(null);
  const run = async (action: Parameters<typeof api.call<'missions.bulk'>>[1]['body']['action'], label: string) => {
    try {
      const r = await api.call('missions.bulk', { body: { ids, action } });
      await refresh();
      onClear();
      toast(`${label} · ${r.updated} ${r.updated === 1 ? t.item : t.items}`, r.undoToken ? { action: { label: 'Undo', onClick: () => void api.call('missions.undo', { body: { undoToken: r.undoToken! } }).then(() => refresh()) } } : undefined);
    } catch (e) {
      onErr(e);
    }
  };
  const addDays = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const menus: Record<keyof typeof refs, MenuItem[]> = {
    status: project.statuses.map((x) => ({ label: x.name, dot: x.color, onClick: () => void run({ type: 'status', statusId: x.id }, `Moved to ${x.name}`) })),
    assignee: members.options.slice(0, 12).map((m) => ({ label: m.label, onClick: () => void run({ type: 'assign', assigneeIds: [m.value], mode: 'add' }, `Assigned ${m.label}`) })),
    due: [
      { label: 'Today', onClick: () => void run({ type: 'due', dueAt: addDays(0) }, 'Due today') },
      { label: 'Tomorrow', onClick: () => void run({ type: 'due', dueAt: addDays(1) }, 'Due tomorrow') },
      { label: 'Next week', onClick: () => void run({ type: 'due', dueAt: addDays(7) }, 'Due next week') },
      '-',
      { label: 'Clear due date', onClick: () => void run({ type: 'due', dueAt: null }, 'Due date cleared') },
    ],
    priority: (Object.keys(PRIORITY) as (keyof typeof PRIORITY)[]).map((p) => ({ label: PRIORITY[p].label, dot: PRIORITY[p].color, onClick: () => void run({ type: 'priority', priority: p }, `Priority ${PRIORITY[p].label}`) })),
    move: flat.filter((p) => p.id !== project.id).map((p) => ({ label: p.name, dot: p.color, onClick: () => void run({ type: 'move', projectId: p.id }, `Moved to ${p.name}`) })),
  };
  const btn = (k: keyof typeof refs, icon: React.ReactNode, label: string) => (
    <>
      <button ref={refs[k]} className="bb" aria-label={label} onClick={() => setOpen(open === k ? null : k)}>{icon}<span>{label}</span></button>
      <Menu anchorRef={refs[k]} open={open === k} onClose={() => setOpen(null)} title={label} items={menus[k]} />
    </>
  );
  return (
    <div className={`bulk${ids.length ? ' show' : ''}`} role="toolbar" aria-label="Bulk actions">
      <span className="cnt"><i>{ids.length}</i><span>{ids.length === 1 ? t.item : t.items} selected</span></span>
      {btn('status', <CircleDot />, 'Status')}
      {btn('assignee', <UserPlus />, 'Assignee')}
      {btn('due', <Calendar />, 'Due date')}
      {btn('priority', <Flag />, 'Priority')}
      {btn('move', <FolderInput />, 'Move')}
      <span className="bsep" />
      {s.can('mission.delete') && (
        <button
          className="bb del"
          aria-label="Delete"
          onClick={async () => {
            if (await dialogs.confirm({ tone: 'danger', title: `Delete ${ids.length} ${ids.length === 1 ? t.item : t.items}?`, message: 'They will be moved to Trash. You can undo this for 30 seconds.', confirmText: `Delete ${ids.length}` })) void run({ type: 'delete' }, 'Deleted');
          }}
        >
          <Trash2 /><span>Delete</span>
        </button>
      )}
      <button className="bb" onClick={onClear} aria-label="Clear selection" data-tooltip="Clear selection"><X /></button>
    </div>
  );
}

export function gridStyle(fields: CustomField[]): CSSProperties {
  const cols = ['36px', 'minmax(340px,1fr)', '104px', '116px', '104px', '72px', ...fields.map((f) => `${cfWidth(f)}px`), '44px'].join(' ');
  const min = 36 + 340 + 104 + 116 + 104 + 72 + fields.reduce((a, f) => a + cfWidth(f), 0) + 44;
  return { ['--c' as string]: cols, ['--minw' as string]: `${min}px` } as CSSProperties;
}

/** Grouped List view: per-group cursor pagination, inline add, bulk selection. */
export function ListView({ project, fields, state }: { project: Project; fields: CustomField[]; state: ListState }) {
  const t = useT();
  const s = useSession();
  const base = useMemo(() => ({ projectId: project.id, groupBy: state.groupBy, sort: state.sort, q: state.q || undefined, filter: filterJson(state) }), [project.id, state]);
  const groups = useQuery({ queryKey: keyFor('missions.groups', { query: base }), queryFn: ({ signal }) => api.call('missions.groups', { query: { ...base, limit: 25 }, signal }) });
  const [sel, setSel] = useState<Set<string>>(new Set());
  const canBulk = s.can('mission.bulk');
  const all = groups.data?.groups.flatMap((g) => g.items.map((m) => m.id)) ?? [];
  const visible = (groups.data?.groups ?? []).filter((g) => state.showClosed || !g.isClosed);
  return (
    <>
      <div className="scroller">
        <div className="grid" style={gridStyle(fields)}>
          <Header fields={fields} canBulk={canBulk} allSel={all.length > 0 && all.every((id) => sel.has(id))} onAll={(on) => setSel(on ? new Set(all) : new Set())} />
          {groups.isPending ? (
            Array.from({ length: 6 }, (_, i) => <SkRow key={i} fields={fields} />)
          ) : groups.isError ? (
            <Empty title={`Couldn’t load ${t.items}`} actions={<button className="btn btn-o" onClick={() => void groups.refetch()}>Try again</button>} />
          ) : visible.every((g) => g.count === 0) ? (
            <Empty title={t.emptyList} text="Try removing a filter or create a new one." />
          ) : (
            visible.map((g) => <GroupBlock key={g.value} g={g} base={base} project={project} fields={fields} sel={sel} setSel={setSel} canBulk={canBulk} canCreate={s.can('mission.create')} />)
          )}
        </div>
      </div>
      <BulkBar ids={[...sel]} project={project} onClear={() => setSel(new Set())} />
    </>
  );
}

/** Table: flat list, every field, infinite scroll (auto-loads near the bottom). */
export function TableView({ project, fields, state }: { project: Project; fields: CustomField[]; state: ListState }) {
  const t = useT();
  const s = useSession();
  const q = useInfinite('missions.list', { query: { projectId: project.id, groupBy: 'none', sort: state.sort === 'manual' ? 'dueAt' : state.sort, q: state.q || undefined, filter: filterJson(state), limit: 25 } });
  const items = flattenPages(q.data);
  const sentinel = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((es) => {
      if (es[0]?.isIntersecting && q.hasNextPage && !q.isFetchingNextPage) void q.fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [q]);
  const canBulk = s.can('mission.bulk');
  return (
    <>
      <div className="view-note">Table view shows every field. More rows load automatically as you scroll to the bottom.</div>
      <div className="scroller">
        <div className="grid" style={gridStyle(fields)}>
          <Header fields={fields} canBulk={canBulk} allSel={items.length > 0 && items.every((m) => sel.has(m.id))} onAll={(on) => setSel(on ? new Set(items.map((m) => m.id)) : new Set())} />
          {q.isPending
            ? Array.from({ length: 8 }, (_, i) => <SkRow key={i} fields={fields} />)
            : items.length
              ? items.map((m) => <Row key={m.id} m={m} project={project} fields={fields} canBulk={canBulk} selected={sel.has(m.id)} onSelect={(id, on) => setSel((x) => { const n = new Set(x); if (on) n.add(id); else n.delete(id); return n; })} />)
              : <Empty title="Nothing found" text={`No ${t.items} match your search.`} />}
          <div className="infinite" ref={sentinel}>
            {q.hasNextPage || q.isFetchingNextPage ? <><Radar size={16} />Loading more… · {items.length} loaded</> : items.length ? `All ${items.length} ${t.items} loaded` : null}
          </div>
        </div>
      </div>
      <BulkBar ids={[...sel]} project={project} onClear={() => setSel(new Set())} />
    </>
  );
}
